import React, { useState, useEffect, useMemo } from "react";
import { CATEGORIES, FUNDS, CONDITIONS, INTAKE_STATUSES, URGENCY_LEVELS, DISTRIBUTION_STATUSES, NEED_STATUSES } from "./api";
import { PhotoUploader } from "../shared/PhotoUploader";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// DonationFormModal — generic modal used for all 3 donations entities:
//   kind = "intake" | "distribution" | "need"
// The shape of fields/options is driven by `kind`.
// ============================================================
export default function DonationFormModal({ kind, existing, currentUser, users, intakes, onSave, onClose }) {
  const todayISO = new Date().toISOString().slice(0, 10);

  const defaultsByKind = {
    intake: {
      itemName: "", category: CATEGORIES[0], fund: FUNDS[0],
      estimatedValue: "", donorName: "", donorPrivate: false, donorConsent: false,
      dateReceived: todayISO, condition: CONDITIONS[0],
      notes: "", photo: "", isPublicListing: false,
      shortDescription: "", status: "Available",
    },
    distribution: {
      intakeId: "", assignedTeamMemberId: currentUser.id, recipientName: "",
      recipientPrivate: false, distributionDate: todayISO, status: "Pending", notes: "",
    },
    need: {
      description: "", category: CATEGORIES[0],
      urgency: URGENCY_LEVELS[1], status: "Open", notes: "",
      dateAdded: todayISO,
    },
  };

  const [form, setForm] = useState(() => existing ? { ...defaultsByKind[kind], ...existing } : defaultsByKind[kind]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const title = useMemo(() => {
    const action = existing ? "Edit" : "New";
    if (kind === "intake")       return `${action} Donation Intake`;
    if (kind === "distribution") return `${action} Distribution`;
    if (kind === "need")         return `${action} Community Need`;
    return action;
  }, [kind, existing]);

  // For the distribution form: choose only intakes that are not already linked
  // to another distribution (unless we're editing the existing one).
  const linkableIntakes = useMemo(() => {
    if (kind !== "distribution") return [];
    return intakes;
  }, [kind, intakes]);

  const submit = async () => {
    setError("");
    if (kind === "intake" && !form.itemName.trim()) { setError("Item name is required."); return; }
    if (kind === "distribution" && !form.intakeId)   { setError("Pick a donated item to distribute."); return; }
    if (kind === "need" && !form.description.trim()) { setError("Need description is required."); return; }
    setSaving(true);
    try {
      const ok = await onSave(form);
      if (ok !== false) { onClose(); return; }
    } catch (e) {
      setError(e.message || "Could not save");
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal donation-modal" data-testid={`donation-form-modal-${kind}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="modal-close" data-testid="donation-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {kind === "intake" && (
            <>
              <Field label="Item Name *">
                <input data-testid="intake-itemName" value={form.itemName} onChange={e => set("itemName", e.target.value)} placeholder="e.g. Winter coats" autoFocus />
              </Field>
              <FieldRow>
                <Field label="Category">
                  <select data-testid="intake-category" value={form.category} onChange={e => set("category", e.target.value)}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Fund">
                  <select data-testid="intake-fund" value={form.fund} onChange={e => set("fund", e.target.value)}>
                    {FUNDS.map(f => <option key={f}>{f}</option>)}
                  </select>
                </Field>
              </FieldRow>
              <FieldRow>
                <Field label={form.category === "Monetary" ? "Amount" : "Estimated Value"}>
                  <input data-testid="intake-value" value={form.estimatedValue} onChange={e => set("estimatedValue", e.target.value)} placeholder="$0.00" />
                </Field>
                <Field label="Date Received">
                  <input data-testid="intake-date" type="date" value={form.dateReceived} onChange={e => set("dateReceived", e.target.value)} />
                </Field>
              </FieldRow>
              <Field label="Donor Name (optional)">
                <input data-testid="intake-donor" value={form.donorName} onChange={e => set("donorName", e.target.value)} placeholder="Leave blank for anonymous" />
              </Field>
              <Toggle testid="intake-donor-consent" label="Donor Consent on File (required to ever display the donor name)" checked={form.donorConsent} onChange={v => set("donorConsent", v)} />
              <Toggle testid="intake-donor-private" label="Keep donor name private (only logger + leader + admins see it)" checked={form.donorPrivate} onChange={v => set("donorPrivate", v)} />
              {!form.donorConsent && form.donorName.trim() && (
                <div style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.40)", borderRadius: 8, padding: "9px 12px", fontSize: 11, color: "#ff9b9b", fontFamily: "'Montserrat',sans-serif", marginBottom: 10, display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <CustomIcon name="warning" size={13} /> <span>Without Donor Consent on File this donor name will <strong>never</strong> appear in any view or report — even to admins.</span>
                </div>
              )}
              <FieldRow>
                <Field label="Condition">
                  <select data-testid="intake-condition" value={form.condition} onChange={e => set("condition", e.target.value)}>
                    {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select data-testid="intake-status" value={form.status} onChange={e => set("status", e.target.value)}>
                    {INTAKE_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              </FieldRow>

              <div style={{ borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", margin: "16px 0 12px", paddingTop: 12 }}>
                <div className="donation-section-label">Public Listing (optional)</div>
                <Toggle testid="intake-public-toggle" label="Show on public listing page" checked={form.isPublicListing} onChange={v => set("isPublicListing", v)} />
                {form.isPublicListing && (
                  <>
                    <Field label="Short description (shown publicly)">
                      <textarea data-testid="intake-shortDescription" rows={2} value={form.shortDescription} onChange={e => set("shortDescription", e.target.value)} placeholder="A one-line summary for the public card." />
                    </Field>
                    <Field label="Photo">
                      <PhotoUploader value={form.photo} onChange={v => set("photo", v)} data-testid="intake-photo" />
                    </Field>
                  </>
                )}
              </div>

              <Field label="Internal notes">
                <textarea data-testid="intake-notes" rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Anything the team should know…" />
              </Field>
            </>
          )}

          {kind === "distribution" && (
            <>
              <Field label="Donated Item *">
                <select data-testid="dist-intakeId" value={form.intakeId} onChange={e => set("intakeId", e.target.value)}>
                  <option value="">— Select an intake item —</option>
                  {linkableIntakes.map(i => (
                    <option key={i.id} value={i.id}>{i.itemName} ({i.category})</option>
                  ))}
                </select>
              </Field>
              <Field label="Assigned Team Member">
                <select data-testid="dist-assignee" value={form.assignedTeamMemberId} onChange={e => set("assignedTeamMemberId", e.target.value)}>
                  {(users || []).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Recipient (optional)">
                <input data-testid="dist-recipient" value={form.recipientName} onChange={e => set("recipientName", e.target.value)} placeholder="Leave blank if anonymous" />
              </Field>
              <Toggle testid="dist-recipient-private" label="Keep recipient name private" checked={form.recipientPrivate} onChange={v => set("recipientPrivate", v)} />
              <FieldRow>
                <Field label="Distribution Date">
                  <input data-testid="dist-date" type="date" value={form.distributionDate} onChange={e => set("distributionDate", e.target.value)} />
                </Field>
                <Field label="Status">
                  <select data-testid="dist-status" value={form.status} onChange={e => set("status", e.target.value)}>
                    {DISTRIBUTION_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              </FieldRow>
              <Field label="Notes">
                <textarea data-testid="dist-notes" rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} />
              </Field>
            </>
          )}

          {kind === "need" && (
            <>
              <Field label="Need Description *">
                <textarea data-testid="need-description" rows={3} value={form.description} onChange={e => set("description", e.target.value)} placeholder="e.g. Winter blankets for shelter outreach" autoFocus />
              </Field>
              <FieldRow>
                <Field label="Category">
                  <select data-testid="need-category" value={form.category} onChange={e => set("category", e.target.value)}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Urgency">
                  <select data-testid="need-urgency" value={form.urgency} onChange={e => set("urgency", e.target.value)}>
                    {URGENCY_LEVELS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </Field>
              </FieldRow>
              <FieldRow>
                <Field label="Date Added">
                  <input data-testid="need-date" type="date" value={form.dateAdded} onChange={e => set("dateAdded", e.target.value)} />
                </Field>
                <Field label="Status">
                  <select data-testid="need-status" value={form.status} onChange={e => set("status", e.target.value)}>
                    {NEED_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              </FieldRow>
              <Field label="Notes">
                <textarea data-testid="need-notes" rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} />
              </Field>
            </>
          )}

          {error && <div className="login-error" data-testid="donation-form-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" data-testid="donation-save" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : (existing ? "Save" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="donation-field">
      <span className="df-label">{label}</span>
      {children}
    </label>
  );
}
function FieldRow({ children }) {
  return <div className="donation-field-row">{children}</div>;
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="donation-toggle" data-testid={`${testid}-label`}>
      <input type="checkbox" data-testid={testid} checked={!!checked} onChange={e => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
